/* York's Shona Gallery: behaviour shared by every page. No jQuery.
   Page sections keep their own scripts; they talk to this file through
   window.YSG and the 'ysg:cart-updated' event. */
(function () {
  'use strict';

  var YSG = (window.YSG = window.YSG || {});

  /* ── Drawers (mobile nav, enquiry panel) ── */
  function drawer(panel, overlay, opts) {
    opts = opts || {};
    var lastFocus = null;
    function isOpen() { return panel.classList.contains('is-open'); }
    function open(trigger) {
      /* Safari does not focus a tapped button, so remember the trigger itself */
      lastFocus = (trigger && trigger.focus) ? trigger : document.activeElement;
      panel.classList.add('is-open', 'is-scroll-locked');
      overlay.classList.add('is-open');
      panel.setAttribute('aria-hidden', 'false');
      if (opts.onOpen) opts.onOpen();
      setTimeout(function () {
        var first = panel.querySelector(opts.focus || 'button') || panel.querySelector('button');
        if (first) first.focus({ preventScroll: true });
      }, 60);
    }
    function close() {
      if (!isOpen()) return;
      panel.classList.remove('is-open', 'is-scroll-locked');
      overlay.classList.remove('is-open');
      panel.setAttribute('aria-hidden', 'true');
      if (opts.onClose) opts.onClose();
      if (lastFocus && lastFocus.focus) lastFocus.focus({ preventScroll: true });
    }
    overlay.addEventListener('click', close);
    document.addEventListener('keydown', function (e) {
      if (!isOpen()) return;
      if (e.key === 'Escape') return close();
      if (e.key !== 'Tab') return;
      /* keep Tab inside the open panel */
      var f = panel.querySelectorAll('a[href], button, input:not([type="hidden"]), textarea, select');
      if (!f.length) return;
      var first = f[0], last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    });
    return { open: open, close: close, isOpen: isOpen };
  }

  /* ── Mobile nav ── */
  function initMobileNav() {
    var nav = document.getElementById('YsgMobileNav');
    var overlay = document.querySelector('.ysg-mobile-overlay');
    var burger = document.querySelector('.ysg-header__hamburger');
    if (!nav || !overlay || !burger) return;
    var d = drawer(nav, overlay, {
      focus: '.ysg-mobile-nav__close',
      onOpen: function () { burger.setAttribute('aria-expanded', 'true'); },
      onClose: function () { burger.setAttribute('aria-expanded', 'false'); }
    });
    burger.addEventListener('click', function () { d.open(burger); });
    nav.querySelector('.ysg-mobile-nav__close').addEventListener('click', d.close);
  }

  /* ── Header height ──
     The sticky header's height varies (the announcement bar hides on phones).
     Sections that stick content under it read --header-h. */
  function initHeaderHeight() {
    var header = document.querySelector('.ysg-header');
    if (!header) return;
    var raf;
    function sync() {
      var h = Math.round(header.getBoundingClientRect().height);
      if (h > 0) document.documentElement.style.setProperty('--header-h', h + 'px');
    }
    function schedule() { cancelAnimationFrame(raf); raf = requestAnimationFrame(sync); }
    sync();
    window.addEventListener('load', sync);
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(sync);
    if (window.ResizeObserver) new ResizeObserver(schedule).observe(header);
    else window.addEventListener('resize', schedule);
  }

  /* ── Enquiry panel ── */
  function initEnquire() {
    var panel = document.getElementById('YsgEnquire');
    var overlay = document.querySelector('.ysg-enquire-overlay');
    if (!panel || !overlay) return;
    var launcher = document.querySelector('.ysg-enquire-launcher');
    var d = drawer(panel, overlay, { focus: 'input:not([type="hidden"]), textarea' });
    YSG.openEnquiry = d.open;

    document.addEventListener('click', function (e) {
      var trigger = e.target.closest('[data-ysg-enquire]');
      if (trigger) { e.preventDefault(); d.open(trigger); }
      else if (e.target.closest('[data-ysg-enquire-close]')) d.close();
    });

    /* Hide the floating launcher while a page's own enquire button or the
       footer is on screen (over the black footer it disappeared). */
    if (launcher && window.IntersectionObserver) {
      var targets = Array.prototype.filter.call(document.querySelectorAll('[data-ysg-enquire]'), function (el) { return el !== launcher; });
      var footer = document.querySelector('.ysg-footer');
      if (footer) targets.push(footer);
      var visible = new Set();
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (en) { en.isIntersecting ? visible.add(en.target) : visible.delete(en.target); });
        launcher.classList.toggle('is-hidden', visible.size > 0);
      });
      targets.forEach(function (el) { io.observe(el); });
    }

    /* Shopify reloads the page after a submit; reopen to show the result */
    if (panel.querySelector('[data-ysg-enquire-open-on-load]')) d.open();
  }

  /* ── Cart ──
     Sections add to cart with YSG.cart.add(); every cart count on the page
     updates from the response. */
  YSG.cart = {
    root: (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/',
    request: function (path, body) {
      return fetch(this.root + path, {
        method: body ? 'POST' : 'GET',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: body ? JSON.stringify(body) : undefined
      }).then(function (r) {
        return r.json().then(function (data) {
          if (!r.ok) throw data;
          return data;
        });
      });
    },
    get: function () { return this.request('cart.js'); },
    add: function (items) {
      var self = this;
      return this.request('cart/add.js', { items: items }).then(function (added) {
        return self.refresh().then(function () { return added; });
      });
    },
    change: function (line, quantity) {
      var self = this;
      return this.request('cart/change.js', { line: line, quantity: quantity }).then(function (cart) {
        self.publish(cart);
        return cart;
      });
    },
    refresh: function () {
      var self = this;
      return this.get().then(function (cart) { self.publish(cart); return cart; });
    },
    publish: function (cart) {
      document.querySelectorAll('[data-cart-count]').forEach(function (el) {
        el.textContent = cart.item_count;
        el.setAttribute('data-count', cart.item_count);
      });
      document.querySelectorAll('[data-cart-link]').forEach(function (el) {
        el.setAttribute('aria-label', 'Cart, ' + cart.item_count + ' items');
      });
      document.dispatchEvent(new CustomEvent('ysg:cart-updated', { detail: { cart: cart } }));
    }
  };

  /* Back/forward cache can show a stale count */
  window.addEventListener('pageshow', function (e) { if (e.persisted) YSG.cart.refresh(); });

  function init() {
    initMobileNav();
    initHeaderHeight();
    initEnquire();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
